import { Blockquote } from '@ui';
import { Selection } from 'components/DataTable';
import { FilterState } from 'components/DataTable/DataTable';
import { Form } from 'components/Form/base/Form';
import { FormInput } from 'components/Form/next';
import { useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../hooks/useApi';
import AccountFormInput from '../Account/AccountFormInput';

interface SkippedInvoice {
    number: string;
    reason: 'cancelled' | 'exported';
}

interface Props {
    filter?: FilterState;
    invoices: Selection;
    selectedCategory?: string | number | false;
    clubSlug: string;
    callback: () => any;
}

/**
 * Assigns one revenue account to the items of every selected invoice — the bulk counterpart to the
 * per-item account picker in the invoice editor, needed because the DATEV export refuses to run
 * while any item is missing one.
 */
export const SetAccountForm = ({ invoices, filter, selectedCategory, callback, clubSlug }: Props) => {
    const { t } = useTranslation('invoice');
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);
    const [skipped, setSkipped] = useState<SkippedInvoice[]>([]);

    const handleSubmit = useCallback(
        (data: FieldValues) => {
            setIsLoading(true);

            return api({
                url: `invoice/${clubSlug}/set-account`,
                method: 'POST',
                data: {
                    account_number: data.account_number,
                    overwrite: !!data.overwrite,
                    filter,
                    invoices: invoices === 'all' ? [] : invoices,
                    select_all: invoices === 'all',
                    selectedCategory
                },
                // the api middleware calls onFinish in a finally, so it also fires on 422
                onFinish: () => setIsLoading(false),
                onSuccess: ({ data: result }) => {
                    // Cancelled and already exported invoices are left untouched on purpose —
                    // say so instead of letting the count silently come up short.
                    if (result?.skipped?.length) {
                        setSkipped(result.skipped);

                        return;
                    }

                    callback?.();
                }
            });
        },
        [api, clubSlug, invoices, filter, selectedCategory, callback]
    );

    return (
        <Form onSubmit={handleSubmit}>
            <Blockquote className="tw-mb-4">{t('setAccount.description')}</Blockquote>
            <AccountFormInput
                name="account_number"
                label={t('setAccount.fields.account')}
                clubSlug={clubSlug}
            />
            <FormInput.Checkbox name="overwrite" label={t('setAccount.fields.overwrite')} />
            <Blockquote className="tw-mt-4">{t('setAccount.overwriteHint')}</Blockquote>
            {skipped.length > 0 && (
                <Blockquote color="warning" className="tw-mt-4">
                    <p className="tw-mb-2">{t('setAccount.skippedHint')}</p>
                    <ul className="tw-list-inside tw-list-disc">
                        {skipped.map((invoice) => (
                            <li key={invoice.number}>
                                {invoice.number} — {t(`setAccount.skippedReason.${invoice.reason}`)}
                            </li>
                        ))}
                    </ul>
                </Blockquote>
            )}
            <FormInput.Submit isLoading={isLoading} />
        </Form>
    );
};
